import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import validator from 'utils/validator';
import { Form, Button, Alert, Card } from 'components/base';
import classNames from 'classnames';
import Icon, { ICON_TYPE } from 'components/icon';

const AUTH_TYPE = {
  SIGN_IN: 'signIn',
  SIGN_UP: 'signUp',
};

const SignInWithEmail = props => {
  const [authType, setAuthType] = useState(props.type);
  const [message, setMessage] = useState();

  const emailInputRef = useRef();
  const passwordInputRef = useRef();

  const handleOnChangeAuthType = value => {
    setAuthType(value);

    setMessage();
  };

  const handleOnSignInFormSubmit = async e => {
    e.preventDefault();

    setMessage();

    const email = emailInputRef.current.value;
    const password = passwordInputRef.current.value;

    if (!email || !password) {
      setMessage({ text: 'Email address and password are required.', type: 'danger' });
      return;
    }

    if (!validator.isEmailValid(email)) {
      setMessage({ text: 'Your email address seems to be invalid.', type: 'danger' });
      return;
    }

    if (!validator.isPasswordValid(password)) {
      setMessage({ text: 'Please fill your password.', type: 'danger' });
      return;
    }

    if (authType === AUTH_TYPE.SIGN_IN) {
      const { success, error } = await props.onSignIn(email, password);
      if (!success) {
        setMessage({
          text:
            error ||
            'The password or the email that you have entered is incorrect. If you have forgotten your password, please use the forgot password link.',
          type: 'danger',
        });
      }
    } else {
      const { success, error } = await props.onSignUp(email, password);
      if (!success) {
        setMessage({
          text:
            error ||
            'The password or the email that you have entered is incorrect. Maybe an account already exists with this email address. Try to sign in instead.',
          type: 'danger',
        });
      }
    }
  };

  const handleOnForgotPasswordClick = async e => {
    e.preventDefault();

    setMessage();

    const email = emailInputRef.current.value;

    if (!email) {
      setMessage({ text: 'Email address is required.', type: 'danger' });
      return;
    }

    if (!validator.isEmailValid(email)) {
      setMessage({ text: 'Your email address seems to be invalid.', type: 'danger' });
      return;
    }

    const { success, error } = await props.onForgetPassword(email);
    if (success) {
      setMessage({
        text: `A link to reset your password has been sent to your email address ${email}.`,
        type: 'success',
      });
    } else {
      setMessage({
        text: error || 'Oops, something went wrong. Please try again.',
        type: 'danger',
      });
    }
  };

  return (
    <Form>
      {false && (
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Input
            type="radio"
            name="authType"
            value={AUTH_TYPE.SIGN_UP}
            checked={authType === AUTH_TYPE.SIGN_UP}
            placeholder="Create new Parabo account"
            onChange={e => handleOnChangeAuthType(AUTH_TYPE.SIGN_UP)}
          />
          <Form.Input
            type="radio"
            name="authType"
            value={AUTH_TYPE.SIGN_IN}
            checked={authType === AUTH_TYPE.SIGN_IN}
            placeholder="Sign In with existing account"
            onChange={e => handleOnChangeAuthType(AUTH_TYPE.SIGN_IN)}
          />
        </Form.Group>
      )}
      <Form.Group>
        <Form.Label htmlFor="email" className="d-flex justify-content-between align-items-end">
          <span>
            E-mail <span className="text-danger">*</span>
          </span>
        </Form.Label>
        <Form.Input ref={emailInputRef} type="email" id="email" placeholder="E-mail" />
      </Form.Group>
      <Form.Group>
        <Form.Label htmlFor="password" className="d-flex justify-content-between align-items-end">
          <span>
            Password <span className="text-danger">*</span>
          </span>
          {authType === AUTH_TYPE.SIGN_IN && (
            <a href="" onClick={handleOnForgotPasswordClick} tabIndex={-1}>
              Forgot your password?
            </a>
          )}
        </Form.Label>
        <Form.Input ref={passwordInputRef} type="password" id="password" placeholder="Password" />
      </Form.Group>

      <Form.Group className="mt-5">
        <Button type="primary" block htmlType="submit" onClick={handleOnSignInFormSubmit}>
          {authType === AUTH_TYPE.SIGN_UP ? 'Create Account' : 'Sign In'}
        </Button>
      </Form.Group>
      <Form.Group className="mb-0">
        <div className="text-center">
          {authType === AUTH_TYPE.SIGN_UP ? (
            <span className="label">
              Already have an account?{' '}
              <a
                href=""
                onClick={e => {
                  e.preventDefault();
                  handleOnChangeAuthType(AUTH_TYPE.SIGN_IN);
                }}
              >
                Sign In
              </a>
            </span>
          ) : (
            <span className="label">
              Don&apos;t have an account yet?{' '}
              <a
                href=""
                onClick={e => {
                  e.preventDefault();
                  handleOnChangeAuthType(AUTH_TYPE.SIGN_UP);
                }}
              >
                Create Account
              </a>
            </span>
          )}
        </div>
      </Form.Group>
      {message && (
        <Form.Group className="mt-3 mb-0">
          <Alert type={message.type} className="mb-0 text-center">
            <span style={{ fontSize: 13 }}>{message.text}</span>
          </Alert>
        </Form.Group>
      )}
    </Form>
  );
};

SignInWithEmail.propTypes = {
  type: PropTypes.oneOf([AUTH_TYPE.SIGN_IN, AUTH_TYPE.SIGN_UP]),
  onSignIn: PropTypes.func,
  onSignUp: PropTypes.func,
  onForgetPassword: PropTypes.func,
};

SignInWithEmail.defaultProps = {
  type: AUTH_TYPE.SIGN_IN,
};

export const SignInWithEmailToggler = ({ key = 'email', toggler, children, className }) => {
  const isOpen = toggler[0] === key;

  return (
    <Card className={classNames(isOpen ? 'border-black' : 'border-white', className)}>
      <Button
        type={isOpen ? 'default' : 'secondary'}
        className="w-100 transition-none"
        size="sm"
        onClick={() => {
          toggler[1](val => (val === key ? undefined : key));
        }}
      >
        <Icon
          type={ICON_TYPE.ENVELOPE}
          className="position-absolute"
          size={20}
          style={{ left: 16 }}
        />
        <span>Continue with E-mail</span>
      </Button>
      <Card.Body className={isOpen ? 'd-block' : 'd-none'}>{children}</Card.Body>
    </Card>
  );
};

export default SignInWithEmail;
